import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useTrace } from '../context/TraceContext';
import { useApp } from '../context/AppContext';
import TraceLabel from '../components/trace/TraceLabel';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { 
  ClipboardCheck, Calendar, Plus, CheckCircle2,
  Clock, Users, ChevronRight
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { audits, formatDate } from '../data/mockData';

const AuditsPage = () => {
  const { pidTraceMode } = useTrace();
  const { isReadOnly } = useApp();

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Scheduled': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Planned': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'In Progress': return 'bg-purple-50 text-purple-700 border-purple-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const selfInspections = audits.filter(a => a.type === 'Self-Inspection');
  const supplierAudits = audits.filter(a => a.type === 'Supplier Audit');

  return (
    <div className="space-y-6" data-testid="audits-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Audits
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Self-inspections and supplier audit management per Schedule-M
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
            Schedule-M §17: Annual Self-Inspection
          </Badge>
          {/* <ReportExportButton 
            title="Audits Report"
            subtitle="Self-inspections and supplier audit management per Schedule-M"
          /> */}
        </div>
      </div>

      <Tabs defaultValue="self-inspections">
        <TabsList className="bg-slate-100 border border-slate-200">
          <TabsTrigger value="self-inspections" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <ClipboardCheck className="h-4 w-4 mr-2" />
            Self-Inspections
          </TabsTrigger>
          <TabsTrigger value="supplier" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <Users className="h-4 w-4 mr-2" />
            Supplier Audits
          </TabsTrigger>
        </TabsList>

        <TabsContent value="self-inspections" className="mt-4 space-y-4">
          <Card className="bg-emerald-50 border border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-emerald-600" />
                  <div>
                    <p className="text-sm font-medium text-emerald-700">Next Self-Inspection Due</p>
                    <p className="text-xs text-emerald-600">Schedule-M requires annual self-inspection program</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-lg px-3 py-1">
                    12 days
                  </Badge>
                  {!isReadOnly && (
                    <Button className="bg-[#fd4816] hover:bg-[#ea380c] text-white" data-testid="schedule-inspection">
                      <Plus className="h-4 w-4 mr-2" />
                      Schedule New
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <ReportSection id="self-inspections-list" title="Self-Inspections" description="Annual self-inspection program">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <TraceLabel prd="PRD §6.6" req="SM-SI-001" />
              <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-slate-600">ID</TableHead>
                  <TableHead className="text-slate-600">Scope</TableHead>
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-slate-600">Scheduled Date</TableHead>
                  <TableHead className="text-slate-600">Auditor</TableHead>
                  <TableHead className="text-slate-600">Findings</TableHead>
                  <TableHead className="text-slate-600 w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selfInspections.map(audit => (
                  <TableRow key={audit.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`audit-row-${audit.id}`}>
                    <TableCell className="font-mono text-xs text-slate-500">{audit.id}</TableCell>
                    <TableCell className="text-sm text-slate-800 font-medium">{audit.scope}</TableCell>
                    <TableCell>
                      <Badge className={`${getStatusColor(audit.status)} text-xs`}>{audit.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{formatDate(audit.scheduledDate)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{audit.auditor}</TableCell>
                    <TableCell>
                      {audit.findings?.length > 0 ? (
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                          {audit.findings.length} Finding(s)
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-500">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          </ReportSection>
        </TabsContent>

        <TabsContent value="supplier" className="mt-4 space-y-4">
          <div className="flex justify-end">
            {!isReadOnly && (
              <Button className="bg-[#fd4816] hover:bg-[#ea380c] text-white" data-testid="new-supplier-audit">
                <Plus className="h-4 w-4 mr-2" />
                Plan Supplier Audit
              </Button>
            )}
          </div>

          <ReportSection id="supplier-audits-list" title="Supplier Audits" description="Supplier audit management">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <TraceLabel prd="PRD §6.6" req="SM-SUP-001" />
              <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-slate-600">ID</TableHead>
                  <TableHead className="text-slate-600">Supplier</TableHead>
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-slate-600">Planned Date</TableHead>
                  <TableHead className="text-slate-600">Team</TableHead>
                  <TableHead className="text-slate-600 w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {supplierAudits.map(audit => (
                  <TableRow key={audit.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`audit-row-${audit.id}`}>
                    <TableCell className="font-mono text-xs text-slate-500">{audit.id}</TableCell>
                    <TableCell className="text-sm text-slate-800 font-medium">{audit.scope}</TableCell>
                    <TableCell>
                      <Badge className={`${getStatusColor(audit.status)} text-xs`}>{audit.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{formatDate(audit.scheduledDate)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{audit.auditor}</TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </TableCell>
                  </TableRow>
                ))}
                {supplierAudits.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                      No supplier audits scheduled
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
          </ReportSection>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuditsPage;
